import React, { useEffect, useMemo, useState } from "react";
import { adminApiFetch, getAdminRole } from "./api";

const formatDateTime = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toISOString().replace("T", " ").slice(0, 19);
};

const formatAppointment = (date, time) => {
    if (!date && !time) return "-";
    if (!date) return String(time || "-");
    if (!time) return String(date);
    return `${date} ${time}`;
};

const formatActor = (row) => {
    if (row?.actor_admin_username) {
        return `${row.actor_admin_username}${row?.actor_admin_role ? ` (${row.actor_admin_role})` : ""}`;
    }
    if (row?.actor_last_name) {
        return `#${row.actor_guest_id} (${row.actor_last_name})`;
    }
    return `#${row?.actor_guest_id ?? "-"}`;
};

function OrderAudit() {
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL || "http://localhost:8000/orders/api";
    const role = getAdminRole();

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionFilter, setActionFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [actorAdminFilter, setActorAdminFilter] = useState("");
    const [createdFrom, setCreatedFrom] = useState("");
    const [createdTo, setCreatedTo] = useState("");
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(500);

    const canAccess = role === "admin_super";

    const loadAuditRows = async () => {
        if (!canAccess) return;
        setIsLoading(true);
        setError("");
        try {
            const params = new URLSearchParams({ limit: String(limit) });
            if (actionFilter) params.set("action", actionFilter);
            if (sourceFilter) params.set("source", sourceFilter);
            if (actorAdminFilter.trim()) params.set("actor_admin_username", actorAdminFilter.trim());
            if (createdFrom) params.set("created_from", createdFrom);
            if (createdTo) params.set("created_to", createdTo);

            const response = await adminApiFetch(`${baseUrl}/order_audit_logs?${params.toString()}`);
            if (!response.ok) {
                throw new Error(`Failed to load order audit entries (${response.status}).`);
            }
            const payload = await response.json();
            setRows(Array.isArray(payload) ? payload : []);
        } catch (err) {
            setError(err?.message || "Could not load audit entries.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAuditRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit, actionFilter, sourceFilter, actorAdminFilter, createdFrom, createdTo]);

    const actions = useMemo(() => {
        const values = [...new Set(rows.map((row) => row?.action).filter(Boolean))];
        values.sort((a, b) => String(a).localeCompare(String(b)));
        return values;
    }, [rows]);

    const sources = useMemo(() => {
        const values = [...new Set(rows.map((row) => row?.source).filter(Boolean))];
        values.sort((a, b) => String(a).localeCompare(String(b)));
        return values;
    }, [rows]);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (!query) return true;
            const haystack = [
                row?.action,
                row?.source,
                row?.actor_last_name,
                row?.actor_admin_username,
                row?.actor_admin_role,
                row?.menu_name,
                row?.order_id,
                row?.order_item_id,
                row?.target_guest_id,
                row?.created_at,
            ]
                .map((value) => String(value ?? "").toLowerCase())
                .join(" ");
            return haystack.includes(query);
        });
    }, [rows, search]);

    if (!canAccess) {
        return <p>Access denied.</p>;
    }

    return (
        <section className="management-page" aria-label="Order Audit">
            <div className="management-page-header">
                <h2 className="management-page-title">Order Audit</h2>
                <div className="management-badge-row">
                    <span className="management-badge management-badge-neutral">Rows: {filteredRows.length}</span>
                </div>
            </div>

            <div className="management-callout">
                Displays persisted audit entries from <code>order_audit_log</code>.
            </div>

            <div className="management-order-header-grid">
                <label className="management-order-field">
                    Action
                    <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
                        <option value="">All</option>
                        {actions.map((action) => (
                            <option key={action} value={action}>{action}</option>
                        ))}
                    </select>
                </label>

                <label className="management-order-field">
                    Source
                    <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                        <option value="">All</option>
                        {sources.map((source) => (
                            <option key={source} value={source}>{source}</option>
                        ))}
                    </select>
                </label>

                <label className="management-order-field">
                    Search
                    <input
                        type="text"
                        placeholder="guest, item, order id..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </label>

                <label className="management-order-field">
                    Admin Username
                    <input
                        type="text"
                        placeholder="e.g. superadmin"
                        value={actorAdminFilter}
                        onChange={(e) => setActorAdminFilter(e.target.value)}
                    />
                </label>

                <label className="management-order-field">
                    Created From
                    <input
                        type="date"
                        value={createdFrom}
                        onChange={(e) => setCreatedFrom(e.target.value)}
                    />
                </label>

                <label className="management-order-field">
                    Created To
                    <input
                        type="date"
                        value={createdTo}
                        onChange={(e) => setCreatedTo(e.target.value)}
                    />
                </label>

                <label className="management-order-field">
                    Max rows
                    <select value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}>
                        <option value="100">100</option>
                        <option value="500">500</option>
                        <option value="1000">1000</option>
                        <option value="5000">5000</option>
                    </select>
                </label>
            </div>

            <div className="management-action-bar">
                <button type="button" onClick={loadAuditRows} disabled={isLoading}>
                    {isLoading ? "Loading..." : "Refresh"}
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setActionFilter("");
                        setSourceFilter("");
                        setActorAdminFilter("");
                        setCreatedFrom("");
                        setCreatedTo("");
                        setSearch("");
                    }}
                    disabled={isLoading}
                >
                    Reset Filters
                </button>
            </div>

            {error ? <div className="management-error">{error}</div> : null}

            <div className="management-table-shell">
                <table className="management-table">
                    <thead>
                    <tr>
                        <th>Created</th>
                        <th>Action</th>
                        <th>Source</th>
                        <th>Actor</th>
                        <th>Target Guest</th>
                        <th>Villa</th>
                        <th>Order</th>
                        <th>Order Item</th>
                        <th>Menu</th>
                        <th>Quantity</th>
                        <th>Appointment</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredRows.map((row) => (
                        <tr key={row.id}>
                            <td>{formatDateTime(row.created_at)}</td>
                            <td>{row.action || "-"}</td>
                            <td>{row.source || "-"}</td>
                            <td>{formatActor(row)}</td>
                            <td>{row.target_guest_id ?? "-"}</td>
                            <td>{row.villa_id ?? "-"}</td>
                            <td>{row.order_id ?? "-"}</td>
                            <td>{row.order_item_id ?? "-"}</td>
                            <td>{row.menu_name ? `${row.menu_name}${row.menu_item_id ? ` (#${row.menu_item_id})` : ""}` : "-"}</td>
                            <td>{row.quantity ?? "-"}</td>
                            <td>{formatAppointment(row.appointment_date, row.appointment_time)}</td>
                        </tr>
                    ))}
                    {!isLoading && filteredRows.length === 0 ? (
                        <tr>
                            <td colSpan={11}>No audit entries found for the current filter.</td>
                        </tr>
                    ) : null}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

export default OrderAudit;

